import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { AlertTriangle, Mail, ChevronRight, RefreshCw, ArrowLeft, FileText, CheckCircle2, Clock } from "lucide-react";

export default function OperationalFollowup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | missing | expired | complete
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");

  const fetchData = () => {
    setLoading(true);
    http.get("/admin/operacion/seguimiento")
      .then((r) => setData(r.data))
      .catch((e) => setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const sendReminder = async (sid, name) => {
    setBusy(sid); setMsg("");
    try {
      await http.post(`/admin/operacion/recordatorio/${sid}`);
      setMsg(`Recordatorio registrado para ${name}.`);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const items = (data?.items || []).filter((s) => {
    if (filter === "all") return true;
    if (filter === "missing") return s.missing_count > 0;
    if (filter === "expired") return s.expired_count > 0;
    if (filter === "complete") return s.missing_count === 0 && s.expired_count === 0;
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Volver al dashboard
      </Link>

      <div className="mt-2 mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">seguimiento operativo</div>
          <h1 className="font-display text-4xl">Documentos faltantes por proveedor</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
            Vista única del estado de documentación de cada proveedor. Permite hacer follow-up sin chasing manual.
          </p>
        </div>
        <button onClick={fetchData} disabled={loading}
          data-testid="refresh-btn"
          className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="op-summary">
          <SummaryCard label="Proveedores" value={data.summary.total_suppliers} tone="blue" onClick={() => setFilter("all")} active={filter === "all"} testid="filter-all" />
          <SummaryCard label="Con faltantes" value={data.summary.with_missing} tone="red" onClick={() => setFilter("missing")} active={filter === "missing"} icon={<AlertTriangle size={14} />} testid="filter-missing" />
          <SummaryCard label="Con expirados" value={data.summary.with_expired} tone="amber" onClick={() => setFilter("expired")} active={filter === "expired"} icon={<Clock size={14} />} testid="filter-expired" />
          <SummaryCard label="Al corriente" value={data.summary.complete} tone="green" onClick={() => setFilter("complete")} active={filter === "complete"} icon={<CheckCircle2 size={14} />} testid="filter-complete" />
        </div>
      )}

      {msg && <div className="mb-4 text-sm bg-[var(--cream-soft)] border border-[var(--lala-blue)] rounded-lg p-2 text-[var(--lala-blue)]" data-testid="op-msg">{msg}</div>}

      {/* List */}
      {loading && <div className="text-center py-12 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center py-12 text-[var(--ink-soft)] italic" data-testid="op-empty">
          No hay proveedores que coincidan con el filtro.
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-3" data-testid="op-list">
          {items.map((s) => (
            <SupplierRow key={s.supplier_id} s={s} criteriaTotal={data.criteria_total} busy={busy === s.supplier_id} onReminder={() => sendReminder(s.supplier_id, s.supplier_name)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, onClick, active, icon, testid }) {
  const tones = {
    blue: "border-[var(--lala-blue)]",
    red: "border-red-400",
    amber: "border-amber-400",
    green: "border-green-500",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`sketch-border-soft bg-[var(--cream-soft)] p-4 text-left transition-all ${tones[tone]} ${active ? "ring-2 ring-[var(--lala-blue)]" : "hover:-translate-y-0.5"}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--ink-soft)]">{icon}{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </button>
  );
}

function SupplierRow({ s, criteriaTotal, busy, onReminder }) {
  const [expanded, setExpanded] = useState(false);
  const pct = s.completion_pct;
  const barColor = pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-400" : "bg-red-500";
  return (
    <div className="sketch-border-soft bg-[var(--cream-soft)] p-4" data-testid={`op-row-${s.supplier_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => setExpanded(!expanded)} className="flex-1 min-w-0 text-left flex items-center gap-3">
          <ChevronRight size={16} className={`text-[var(--lala-blue)] transition-transform ${expanded ? "rotate-90" : ""}`} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[var(--ink)] truncate">{s.supplier_name}</div>
            <div className="text-xs text-[var(--ink-soft)] flex flex-wrap gap-x-3">
              <span>{s.category}</span>
              {s.contact_email && <span>{s.contact_email}</span>}
              {s.days_idle !== null && <span>· última actividad hace {s.days_idle}d</span>}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-[var(--ink-soft)] uppercase tracking-wider">Completitud</div>
            <div className="font-display text-xl text-[var(--lala-blue)]">{pct}%</div>
          </div>
          <div className="w-32">
            <div className="h-2 bg-[var(--cream)] rounded-full overflow-hidden border border-[var(--line)]">
              <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {s.missing_count > 0 && (
            <span className="text-xs font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5"
              data-testid={`badge-missing-${s.supplier_id}`}>
              {s.missing_count} faltante{s.missing_count !== 1 && "s"}
            </span>
          )}
          {s.expired_count > 0 && (
            <span className="text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
              {s.expired_count} expirado{s.expired_count !== 1 && "s"}
            </span>
          )}
          {s.pending_count > 0 && (
            <span className="text-xs font-semibold bg-stone-50 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5">
              {s.pending_count} en revisión
            </span>
          )}
          <button onClick={onReminder} disabled={busy}
            data-testid={`reminder-btn-${s.supplier_id}`}
            className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50">
            <Mail size={12} /> {busy ? "…" : "Recordar"}
          </button>
          <Link to={`/proveedores/${s.supplier_id}`}
            className="text-xs text-[var(--lala-blue)] font-semibold hover:underline">Ver →</Link>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pl-7 grid sm:grid-cols-3 gap-4 text-sm" data-testid={`op-detail-${s.supplier_id}`}>
          <DocList title="Faltantes" items={s.missing} color="text-red-700" empty="—" />
          <DocList title="Expirados" items={s.expired} color="text-amber-700" empty="—" />
          <DocList title="En revisión" items={s.pending} color="text-stone-700" empty="—" />
        </div>
      )}
    </div>
  );
}

function DocList({ title, items, color, empty }) {
  return (
    <div>
      <div className={`text-xs font-semibold uppercase tracking-wider ${color} mb-1`}>{title}</div>
      <ul className="space-y-0.5">
        {items.length === 0 && <li className="text-[var(--ink-soft)] italic text-xs">{empty}</li>}
        {items.map((d) => (
          <li key={d.code} className="text-xs flex items-center gap-1.5">
            <FileText size={10} className="text-[var(--ink-soft)]" />
            <span title={d.code}>{d.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
